import angular from 'angular';

const defaults = {
  maxAge: Number.MAX_VALUE,
  deleteOnExpire: false,
  onExpire: null,
  storageMode: 'sessionStorage',
  storagePrefix: 'cachefactory.caches.',
  storeOnResolve: false,
  storeOnReject: false
};
const caches = {};
const deps = {};

// TODO move to common utils
function isObject(obj) {
  return obj === Object(obj);
}

function isNumber(value) {
  return typeof value === 'number';
}

function isPromise(value) {
  return value && typeof value.then === 'function';
}
// endof TODO

class LocalService {

  constructor($q) {
    deps.$q = $q;
    this.storage = this.defaults.storageMode;
  }

  get defaults() {
    return this.constructor.defaults;
  }

  get storage() {
    return this._storage;
  }

  set storage(type) {
    const storageTypes = {
      localStorage,
      sessionStorage
    };
    this._storage = storageTypes[type];
  }

  createCache(cacheId, options) {
    if (cacheId in caches) {
      return caches[cacheId];
    } else if (!cacheId) {
      throw new Error('cacheId must be a string!');
    }

    let $$promises = {};
    let $$storage = null;
    let $$expiresHeap = [];

    const cache = caches[cacheId] = {

      $$id: cacheId,

      destroy() {
        this.removeAll();
        $$storage.remove(this.$$prefix + '.keys');
        $$storage.remove(this.$$prefix);
        $$storage = null;
        $$expiresHeap = null;
        this.$$prefix = null;
        delete caches[this.$$id];
      },
      get(key, options) {
        options = options || {};
        if (!key) {
          throw new Error('key must be a string');
        }
        if ($$promises[key]) {
          return $$promises[key];
        }
        const item = $$storage.get(this.$$prefix + '.data.' + key);
        if (!item) {
          return undefined;
        }
        let value = item.value;
        const now = new Date().getTime();
        item.accessed = now;
        if (this.$$deleteOnExpire && 'expires' in item && item.expires < now) {
          this.remove(key);

          if (this.$$onExpire) {
            this.$$onExpire(key, item.value, options.onExpire);
          } else if (options.onExpire) {
            options.onExpire.call(this, key, item.value);
          }
          value = undefined;
        } else {
          $$storage.set(this.$$prefix + '.data.' + key, item);
        }
        return value;
      },
      keys() {
        return $$storage.get(this.$$prefix + '.keys') || [];
      },
      put(key, value, options) {
        const me = this;

        options = options || {};

        const storeOnResolve = 'storeOnResolve' in options ? !!options.storeOnResolve : this.$$storeOnResolve;
        const storeOnReject = 'storeOnReject' in options ? !!options.storeOnReject : this.$$storeOnReject;

        const getHandler = (store, isError) => {
          return (v) => {
            if (store) {
              delete $$promises[key];
              if (isObject(v) && 'status' in v && 'data' in v) {
                v = [v.status, v.data, v.headers(), v.statusText];
                me.put(key, v);
              } else {
                me.put(key, v);
              }
            }
            if (isError) {
              return deps.$q.reject(v);
            }
            return v;
          };
        };

        if (value === null || value === undefined) {
          return value;
        }
        if (!key) {
          throw new Error('key must contain a string!');
        }

        const now = new Date().getTime();
        const item = {
          key,
          value: isPromise(value) ?
            value.then(getHandler(storeOnResolve, false), getHandler(storeOnReject, true)) :
            value,
          created: options.created === undefined ? now : options.created,
          accessed: options.accessed === undefined ? now : options.accessed
        };
        if (options.maxAge) {
          item.maxAge = options.maxAge;
        }

        if (options.expires === undefined) {
          item.expires = item.created + (item.maxAge || this.$$maxAge);
        } else {
          item.expires = options.expires;
        }

        if (isPromise(item.value)) {
          $$promises[key] = item.value;
          return item.value;
        }

        // Remove existing
        if ($$storage.get(this.$$prefix + '.data.' + key)) {
          this.remove(key);
        }
        // Add to expires heap
        $$expiresHeap.push({
          key,
          expires: item.expires
        });

        const keys = this.keys();

        // Set item
        $$storage.set(this.$$prefix + '.data.' + key, item);
        let exists = false;
        for (let i = 0; i < keys.length; i++) {
          if (keys[i] === key) {
            exists = true;
            break;
          }
        }
        if (!exists) {
          keys.push(key);
        }
        $$storage.set(this.$$prefix + '.keys', keys);
        return value;
      },
      remove(key) {
        key += ''; // convert number keys to string
        delete $$promises[key];
        const item = $$storage.get(this.$$prefix + '.data.' + key);
        if (!item) {
          return null;
        }

        // search and remove element based on key
        for (let i = 0, len = $$expiresHeap.length; i < len; i++) {
          if (key === $$expiresHeap[i].key) {
            $$expiresHeap.splice(i, 1);
            break;
          }
        }
        $$storage.remove(this.$$prefix + '.data.' + key);
        const keys = $$storage.get(this.$$prefix + '.keys') || [];
        const index = keys.indexOf(key);

        if (index >= 0) {
          keys.splice(index, 1);
        }
        $$storage.set(this.$$prefix + '.keys', keys);
        return item.value;
      },
      removeAll() {
        $$expiresHeap = [];
        const keys = $$storage.get(this.$$prefix + '.keys');

        if (keys) {
          for (let i = 0; i < keys.length; i++) {
            this.remove(keys[i]);
          }
        }
        $$storage.set(this.$$prefix + '.keys', []);
        $$promises = {};
      },
      removeExpired() {
        const now = new Date().getTime();
        const expired = {};
        let key;

        // search and remove elements that expired
        for (let i = 0, len = $$expiresHeap.length; i < len; i++) {
          const expiredItem = $$expiresHeap[i];
          if (expiredItem.expires <= now) {
            expired[expiredItem.key] = expiredItem.value || null;
          }
        }

        for (key in expired) {
          if ({}.hasOwnProperty.call(expired, key)) {
            const item = $$storage.get(this.$$prefix + '.data.' + key);
            if (item) {
              expired[key] = item.value;
              this.remove(key);
            }
          }
        }

        if (this.$$onExpire) {
          for (key in expired) {
            if ({}.hasOwnProperty.call(expired, key)) {
              this.$$onExpire(key, expired[key]);
            }
          }
        }

        return expired;
      },
      get maxAge() {
        return this.$$maxAge;
      },
      set maxAge(maxAge) {
        if (maxAge === null) {
          this.$$maxAge = Number.MAX_VALUE;
        }
        if (!isNumber(maxAge)) {
          throw new Error('maxAge must be a number');
        }
        if (maxAge < 0) {
          throw new Error('maxAge must be greater than zero');
        }
        this.$$maxAge = maxAge;
        let i;
        let key;

        $$expiresHeap = [];

        const keys = $$storage.get(this.$$prefix + '.keys') || [];

        for (i = 0; i < keys.length; i++) {
          key = keys[i];
          const item = $$storage.get(this.$$prefix + '.data.' + key);

          if (item) {
            if (this.$$maxAge === Number.MAX_VALUE) {
              item.expires = Number.MAX_VALUE;
            } else {
              item.expires = item.created + (item.maxAge || this.$$maxAge);
            }
            $$expiresHeap.push({
              key,
              expires: item.expires
            });
          }
        }

        if (this.$$deleteOnExpire) {
          return this.removeExpired();
        }
        return {};
      },
      setOptions(cacheOptions) {
        cacheOptions = cacheOptions || {};
        if (!isObject(cacheOptions)) {
          throw new Error('cacheOptions must be an object!');
        }

        $$storage = new LocalService(deps.$q);

        this.$$storagePrefix = defaults.storagePrefix;
        if ('storagePrefix' in cacheOptions) {
          this.$$storagePrefix = cacheOptions.storagePrefix;
        }

        this.$$prefix = this.$$storagePrefix + this.$$id;

        this.storageMode = defaults.storageMode;
        if ('storageMode' in cacheOptions) {
          this.storageMode = cacheOptions.storageMode || defaults.storageMode;
        }

        this.$$deleteOnExpire = defaults.deleteOnExpire;
        if ('deleteOnExpire' in cacheOptions) {
          this.$$deleteOnExpire = cacheOptions.deleteOnExpire;
        }

        this.maxAge = defaults.maxAge;
        if ('maxAge' in cacheOptions) {
          this.maxAge = cacheOptions.maxAge;
        }

        this.$$storeOnResolve = defaults.storeOnResolve;
        if ('storeOnResolve' in cacheOptions) {
          this.$$storeOnResolve = !!cacheOptions.storeOnResolve;
        }

        this.$$storeOnReject = defaults.storeOnReject;
        if ('storeOnReject' in cacheOptions) {
          this.$$storeOnReject = !!cacheOptions.storeOnReject;
        }

        this.$$onExpire = defaults.onExpire;
        if ('onExpire' in cacheOptions) {
          this.$$onExpire = cacheOptions.onExpire;
        }
      },
      get storageMode() {
        return $$storage.storage;
      },
      set storageMode(storageMode) {
        if (!storageMode) {
          throw new Error('storageMode must be a string!');
        }
        if (storageMode !== 'localStorage' && storageMode !== 'sessionStorage') {
          throw new Error('storageMode must be "localStorage" or "sessionStorage"!');
        }
        $$storage.storage = storageMode;
      },
      values() {
        const keys = this.keys();
        const items = [];
        for (let i = 0; i < keys.length; i++) {
          items.push(this.get(keys[i]));
        }
        return items;
      }
    };

    cache.setOptions(options);

    return cache;
  }

  get(key) {
    try {
      const val = this.storage.getItem(key);
      return val ? JSON.parse(val) : null;
    } catch (e) {
      return undefined;
    }
  }
  set(key, val) {
    if (val !== undefined) {
      this.storage.setItem(key, JSON.stringify(val));
    }
  }
  remove(key) {
    return this.storage.removeItem(key);
  }

  static factory($q) {
    return new LocalService($q);
  }
}

LocalService.defaults = defaults;

LocalService.factory.$inject = ['$q'];

const module = angular.module('localService', [])
  .factory('localService', LocalService.factory);

export default module;



/** WEBPACK FOOTER **
 ** ./client/app/services/localService/localService.js
 **/